import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackFormEvents } from '../../../../lib/analyticsEnhanced';
import { Reminder } from '../../../../types/task';
import { BottomSheetModal } from '../../../../components/v3/BottomSheetModal';
import { IOSRemindersEditor } from '../ios/IOSRemindersEditor';

interface TaskReminderEditorProps {
  isOpen: boolean;
  onClose: () => void;
  reminders: Reminder[];
  onSave: (reminders: Reminder[]) => void | Promise<void>;
  taskTimes?: {
    startAt?: string;
    dueAt?: string;
    cost?: number;
  };
}

/**
 * 提醒设置弹窗组件
 * 保持原有 IOSRemindersEditor 的完整功能
 */
export const TaskReminderEditor: React.FC<TaskReminderEditorProps> = ({
  isOpen,
  onClose,
  reminders,
  onSave,
  taskTimes
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);

  // 本地状态
  const [localReminders, setLocalReminders] = useState<Reminder[]>(reminders);

  // 当弹窗打开时重置本地状态
  useEffect(() => {
    if (isOpen) {
      setLocalReminders(reminders);
    }
  }, [isOpen, reminders]);

  // 处理保存
  const handleSave = async () => {
    // 记录编辑器保存事件
    trackFormEvents.editorSaved('reminder_editor', { reminders: localReminders }, {
      reminder_count: localReminders.length,
      has_reminders: localReminders.length > 0,
      reminder_types: localReminders.map(r => r.type).join(',')
    });

    await onSave(localReminders);
    onClose();
  };

  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('v3/tasks:editors.reminderSettings.title')}
      onConfirm={handleSave}
      confirmText={t('v3/tasks:editors.reminderSettings.save')}
      cancelText={t('v3/tasks:editors.reminderSettings.cancel')}
      useGrayBackground={true}
      usePortal={true}
    >
      <div className="p-4">
        {/* 使用原有的 IOSRemindersEditor 组件 */}
        <IOSRemindersEditor
          reminders={localReminders}
          onChange={setLocalReminders}
          taskTimes={taskTimes}
        />
      </div>
    </BottomSheetModal>
  );
};
